import { ShippingMethodThirdPartyProvider } from '@/helpers/entities/enums/v2/ShippingMethodThirdPartyProvider';
import { GrabExpressDeliveryStatus } from '@/helpers/entities/enums/v2/GrabExpressDeliveryStatus';
import moment from 'moment';
import { omit } from 'lodash';
import { InsuredPersonalDetailModel } from './InsuredPersonalDetailModel';
import { CartItemModel } from './CartItemModel';
import { PaymentStatuses } from '../enums/v2/PaymentStatuses';
import { PaymentOptionType } from '../enums/v2/PaymentOptionType';
import { OrderStatuses } from '../enums/v2/OrderStatuses';
import { ShippingMethodModel } from './ShippingMethodModel';
const dateTimeFormat = 'ddd, MMMM Do YYYY hh:mm a';
export class OrderFullModel {
    constructor(payload) {
        const params = omit(payload, ['items', 'shippingMethod']);
        Object.assign(this, params);
        const { shippingMethod } = payload;
        if (shippingMethod) {
            this.shippingMethod = new ShippingMethodModel(shippingMethod);
        }
        const { insuredPersonalDetail } = payload;
        if (insuredPersonalDetail) {
            this.insuredPersonalDetail = new InsuredPersonalDetailModel(insuredPersonalDetail);
        }
        const node = (item) => {
            return new CartItemModel(item.node);
        };
        this.items = payload.items.edges.map(node);
    }
    get formattedCreatedAt() {
        const createdAt = new Date(this.createdAt);
        return moment(createdAt).format(dateTimeFormat);
    }
    get formattedAdvanceOrderDate() {
        if (!this.advanceOrderDate) {
            return 'None';
        }
        const advanceOrderDate = new Date(this.advanceOrderDate);
        return moment(advanceOrderDate).format(dateTimeFormat);
    }
    get paymentStatusMessage() {
        if (this.paymentOption.paymentType === PaymentOptionType.bank) {
            if (this.orderStatus.paymentStatus === PaymentStatuses.payPending) {
                return 'Waiting For Deposit Slip Upload';
            }
            return this.orderStatus.paymentStatus.replace('_', ' ');
        }
        return this.orderStatus.paymentStatus.replace('_', ' ');
    }
    get formattedShipOutDate() {
        if (!this.orderStatus.shipOutDate) {
            return 'None';
        }
        const shipOutDate = new Date(this.orderStatus.shipOutDate);
        return moment(shipOutDate).format(dateTimeFormat);
    }
    get formattedEstimatedArriveDate() {
        if (!this.orderStatus.estimateArriveDate) {
            return 'None';
        }
        const arriveDate = new Date(this.orderStatus.estimateArriveDate);
        return moment(arriveDate).format(dateTimeFormat);
    }
    get canAddShipOutDetails() {
        if (this.paymentOption.paymentType === PaymentOptionType.creditCard ||
            this.paymentOption.paymentType === PaymentOptionType.thirdParty) {
            return this.orderStatus.status === OrderStatuses.pending && !this.orderStatus.shipOutDate;
        }
        return this.canBeRejected;
    }
    get forKycApproval() {
        return (this.requiredKyc && !this.kycApprovedAt && this.orderStatus.status === OrderStatuses.pending);
    }
    get failedThirdParty() {
        const shippingProvider = ShippingMethodThirdPartyProvider.GrabExpress;
        return (this.orderStatus.status === OrderStatuses.pending &&
            this.shippingMethod?.thirdPartyProvider === shippingProvider &&
            this.paymentOption.paymentType === PaymentOptionType.thirdParty);
    }
    get failedCreditCard() {
        const shippingProvider = ShippingMethodThirdPartyProvider.GrabExpress;
        return (this.orderStatus.status === OrderStatuses.pending &&
            this.shippingMethod?.thirdPartyProvider === shippingProvider &&
            this.paymentOption.paymentType === PaymentOptionType.creditCard);
    }
    get canBeRejected() {
        return this.orderStatus.status === OrderStatuses.pending;
    }
    get canBeMarkAsCompleted() {
        return this.orderStatus.status === OrderStatuses.forDelivery;
    }
    get canBeRebook() {
        const grabExpressProvider = ShippingMethodThirdPartyProvider.GrabExpress;
        const lalamoveProvider = ShippingMethodThirdPartyProvider.Lalamove;
        const pandagoProvider = ShippingMethodThirdPartyProvider.PandaGo;
        const jntExpressProvider = ShippingMethodThirdPartyProvider.JntExpress;
        const lalamoveRebookableStatus = ['EXPIRED', 'REJECTED', 'CANCELED'];
        const pandagoRebookableStatus = ['CANCELLED'];
        const jntExpressRebookableStatus = ['CANCELLED'];
        const deliveryStatus = GrabExpressDeliveryStatus.Failed;
        if (this.shippingMethod?.thirdPartyProvider === grabExpressProvider) {
            return ((this.shippingMethod?.thirdPartyProvider === grabExpressProvider && this.isCancelled) ||
                (this.courierDeliveryStatus === deliveryStatus && this.isForDelivery));
        }
        if (this.shippingMethod?.thirdPartyProvider === lalamoveProvider) {
            return ((this.shippingMethod?.thirdPartyProvider === lalamoveProvider && this.isCancelled) ||
                (lalamoveRebookableStatus.includes(this.courierDeliveryStatus || '') && this.isForDelivery));
        }
        if (this.shippingMethod?.thirdPartyProvider === pandagoProvider) {
            return ((this.shippingMethod?.thirdPartyProvider === pandagoProvider && this.isCancelled) ||
                (pandagoRebookableStatus.includes(this.courierDeliveryStatus || '') && this.isForDelivery));
        }
        if (this.shippingMethod?.thirdPartyProvider === jntExpressProvider) {
            return ((this.shippingMethod?.thirdPartyProvider === jntExpressProvider && this.isCancelled) ||
                (jntExpressRebookableStatus.includes(this.courierDeliveryStatus || '') && this.isForDelivery));
        }
        return false;
    }
    get canBeCancelled() {
        const shippingProvider = ['grab_express', 'lalamove', 'pandago', 'jnt_express'];
        const cancellableStatus = [
            'ALLOCATING',
            'PICKING_UP',
            'ON_GOING',
            'ASSIGNING_DRIVER',
            'RECEIVED',
            'WAITING_FOR_TRANSPORT',
            'ASSIGNED_TO_TRANSPORT',
            'ORDER_CREATED',
            'CANCELLED',
        ];
        if (this.shippingMethod?.thirdPartyProvider) {
            return (shippingProvider?.includes(this.shippingMethod?.thirdPartyProvider || '') &&
                cancellableStatus?.includes(this.courierDeliveryStatus || '') &&
                !this.isCancelled);
        }
        return !this.isCancelled;
    }
    get canCancelGrabExpressBooking() {
        const shippingProvider = ShippingMethodThirdPartyProvider.GrabExpress;
        const cancellableStatus = ['ALLOCATING', 'PICKING_UP'];
        return (this.shippingMethod?.thirdPartyProvider === shippingProvider &&
            this.isCompleted &&
            cancellableStatus.includes(this.courierDeliveryStatus || ''));
    }
    get canCancelLalamoveBooking() {
        const shippingProvider = ShippingMethodThirdPartyProvider.Lalamove;
        const cancellableStatus = ['ON_GOING', 'ASSIGNING_DRIVER'];
        return (this.shippingMethod?.thirdPartyProvider === shippingProvider &&
            this.isCompleted &&
            cancellableStatus.includes(this.courierDeliveryStatus || ''));
    }
    get canCancelPandagoBooking() {
        const shippingProvider = ShippingMethodThirdPartyProvider.PandaGo;
        const cancellableStatus = ['RECEIVED', 'WAITING_FOR_TRANSPORT', 'ASSIGNED_TO_TRANSPORT'];
        return (this.shippingMethod?.thirdPartyProvider === shippingProvider &&
            this.isCompleted &&
            cancellableStatus.includes(this.courierDeliveryStatus || ''));
    }
    get canCancelJntExpressBooking() {
        const shippingProvider = ShippingMethodThirdPartyProvider.JntExpress;
        const cancellableStatus = ['ORDER_CREATED'];
        return (this.shippingMethod?.thirdPartyProvider === shippingProvider &&
            cancellableStatus.includes(this.courierDeliveryStatus || ''));
    }
    get canCreateShopifyOrder() {
        if (this.isForDelivery || this.isCompleted || this.isPending) {
            return !this.apiOrderId;
        }
        return false;
    }
    get isCompleted() {
        return this.orderStatus.status === OrderStatuses.completed;
    }
    get isForDelivery() {
        return this.orderStatus.status === OrderStatuses.forDelivery;
    }
    get isPending() {
        return this.orderStatus.status === OrderStatuses.pending;
    }
    get isCancelled() {
        return this.orderStatus.status === OrderStatuses.cancelled;
    }
    get paymentIsPending() {
        return this.orderStatus.paymentStatus === PaymentStatuses.payPending;
    }
    get paymentForConfirmation() {
        return this.orderStatus.paymentStatus === PaymentStatuses.forConfirmation;
    }
    get paymentIsSuccess() {
        return this.orderStatus.paymentStatus === PaymentStatuses.paySuccess;
    }
    /**
     * Alias to Customer Full Name
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get customerFullName() {
        return `${this.firstName} ${this.lastName}`;
    }
    /**
     * Alias to Order Contact Number with Country Code
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get contactNumberWithCode() {
        return `${this.contactDialCode} ${this.contactNumber}`;
    }
    /**
     * Alias to Order Grand Total with Currency
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get grandTotalWithCurrency() {
        return `${this.currency} ${this.grandTotal}`;
    }
    /**
     * Alias to Order Status
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get status() {
        return this.orderStatus.status;
    }
    /**
     * Humanized Order Status
     * Replace underscore with whitespace
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get humanizedStatus() {
        return this.status.replace('_', ' ');
    }
    /**
     * Alias to Confirmed At
     * @author Jimson Lomio <jimson@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get confirmedAt() {
        return this.orderStatus.confirmedAt;
    }
    /**
     * Alias to Completed At
     * @author Jimson Lomio <jimson@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get completedAt() {
        return this.orderStatus.completedAt;
    }
    /**
     * Alias to Cancelled At
     * @author Jimson Lomio <jimson@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get cancelledAt() {
        return this.orderStatus.cancelledAt;
    }
    /**
     * Alias to Cancel Message
     * @author Jimson Lomio <jimson@gorated.ph>
     * @since Version 0.1.6
     *
     * @return string
     */
    get cancelMessage() {
        return this.orderStatus.cancelMessage;
    }
    /**
     * Alias to ThirdParty Shipping Type
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.4.2}
     *
     * @return {boolean}
     */
    get isShippedUsingThirdParty() {
        const shipping = this.shippingMethod;
        return !!shipping && shipping.isThirdParty;
    }
    /**
     * Ship Out Date in Short Date Format
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.4.2}
     *
     * @return {string}
     */
    get shipOutDateShort() {
        const shipOut = this.orderStatus.shipOutDate;
        if (!shipOut) {
            return 'None';
        }
        return moment(shipOut).format(dateTimeFormat);
    }
    /**
     * Arrival Date in Short Date Format
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.4.2}
     *
     * @return {string}
     */
    get arrivalDateShort() {
        const arrival = this.orderStatus.estimateArriveDate;
        if (!arrival) {
            return 'None';
        }
        return moment(arrival).format(dateTimeFormat);
    }
    get formattedPlateNumber() {
        return this.plateNumber?.toUpperCase();
    }
    get discount() {
        if (!this.promoCode) {
            return 0;
        }
        const { value, valueType } = this.promoCode;
        if (valueType === 'absolute') {
            return value > this.subtotal ? this.subtotal : value;
        }
        return this.subtotal * (value / 100);
    }
}
